/**
 * Risks Query Hooks — cached-instant rendering for RisksScreen.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { risksApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';

interface RisksListParams {
  page?: number;
  pageSize?: number;
  department_id?: number;
  severity?: string;
  status?: string;
  search?: string;
  year?: number;
  // Risk type + severity filters used by RisksScreen.
  risk_type?: string;
  risk_level?: string;
  // Relation filters for "risks linked to a specific entity".
  related_id?: number | string;
  related_type?: string;
}

interface RisksListResponse {
  results: any[];
  count: number;
}

export function useRisksQuery(params?: RisksListParams) {
  return useQuery({
    queryKey: queryKeys.risks.list(params),
    queryFn: async (): Promise<RisksListResponse> => {
      const queryParams: Record<string, any> = {};
      if (params?.page) queryParams.page = params.page;
      if (params?.pageSize) queryParams.page_size = params.pageSize;
      if (params?.department_id) queryParams.department_id = params.department_id;
      if (params?.severity) queryParams.severity = params.severity;
      if (params?.status) queryParams.status = params.status;
      if (params?.search) queryParams.search = params.search;
      if (params?.year) queryParams.year = params.year;
      if (params?.risk_type) queryParams.risk_type = params.risk_type;
      if (params?.risk_level) queryParams.risk_level = params.risk_level;
      if (params?.related_id) queryParams.related_id = params.related_id;
      if (params?.related_type) queryParams.related_type = params.related_type;

      return await risksApi.list(queryParams);
    },
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useRiskDetailQuery(riskId: number) {
  return useQuery({
    queryKey: queryKeys.risks.detail(riskId),
    queryFn: () => risksApi.get(riskId),
    enabled: !!riskId,
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useDeleteRiskMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (riskId: number) => risksApi.delete(riskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });
    },
  });
}

export default useRisksQuery;
